// Mirrors the transfer limits the preview enforces on the whole selection.
export const MAX_PER_KIND = 200
export const MAX_TOTAL = 400

export type Counts = {
  diagrams: number
  playbooks: number
  call_sheets: number
  wristbands: number
}
export type Pickable = { public_id: string; component: string; counts: Counts }

const kinds = ["diagrams", "playbooks", "call_sheets", "wristbands"] as const
const empty = (): Counts => ({ diagrams: 0, playbooks: 0, call_sheets: 0, wristbands: 0 })

// Components are disjoint, so a selection's size is the sum over its distinct components.
function sizeOf(items: Pickable[]): Counts {
  const seen = new Set<string>()
  const total = empty()
  for (const item of items) {
    if (seen.has(item.component)) continue
    seen.add(item.component)
    for (const k of kinds) total[k] += item.counts[k]
  }
  return total
}
export function withinLimits(items: Pickable[]) {
  const c = sizeOf(items)
  return (
    kinds.every((k) => c[k] <= MAX_PER_KIND) && kinds.reduce((n, k) => n + c[k], 0) <= MAX_TOTAL
  )
}

/** Whole components, in list order, until the next one would exceed a transfer's limits. */
export function selectGreedy(movable: Pickable[]) {
  const chosen: Pickable[] = []
  const taken = new Set<string>()
  let stopped = false
  for (const item of movable) {
    if (taken.has(item.component)) {
      chosen.push(item)
      continue
    }
    const group = movable.filter((m) => m.component === item.component)
    if (withinLimits([...chosen, ...group])) {
      taken.add(item.component)
      chosen.push(...group.filter((g) => !chosen.includes(g)))
    } else stopped = true
  }
  const ids = new Set(chosen.map((c) => c.public_id))
  return { ids: movable.filter((m) => ids.has(m.public_id)).map((m) => m.public_id), stopped }
}

export function limitMessage(selectedCount: number, movableCount: number) {
  return `Selected ${selectedCount} of ${movableCount} movable plays. The rest would exceed one transfer's limit of ${MAX_TOTAL} plays and collections (${MAX_PER_KIND} of any one kind); transfer them next.`
}
